/**
 * Filtros avançados para imóveis com design chip-based (estilo Linear/Attio).
 *
 * Filtros ativos são exibidos como chips clicáveis. Novos filtros
 * são adicionados via popover com categorias e seletor de valores.
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { FilterChip } from '../../../crm/leads/components/FilterChip';
import { Icon } from '@/shared/icons'
import type { IconName } from '@/shared/icons'
// ═══════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════

/**
 * Interface de filtros para imóveis
 */
export interface PropertiesFilters {
  /** Tipo de operação: venda, aluguel, etc. */
  opportunityType?: string[];
  /** Tipo de imóvel: casa, apartamento, etc. */
  propertyType?: string[];
  /** Status do imóvel */
  itemStatus?: string[];
  /** Quantidade mínima de quartos */
  minBedrooms?: number;
  /** Quantidade máxima de quartos */
  maxBedrooms?: number;
  /** Quantidade mínima de banheiros */
  minBathrooms?: number;
  /** Quantidade máxima de banheiros */
  maxBathrooms?: number;
  /** Quantidade mínima de vagas */
  minParkingSpaces?: number;
  /** Quantidade máxima de vagas */
  maxParkingSpaces?: number;
  /** Preço mínimo */
  minPrice?: number;
  /** Preço máximo */
  maxPrice?: number;
  /** Área mínima (m²) */
  minArea?: number;
  /** Área máxima (m²) */
  maxArea?: number;
  /** Cidade */
  city?: string;
  /** Bairro */
  neighborhood?: string;
}

// ═══════════════════════════════════════════════════════
// FILTER OPTIONS
// ═══════════════════════════════════════════════════════

interface FilterOption {
  value: string;
  label: string;
}

const OPPORTUNITY_TYPE_OPTIONS: FilterOption[] = [
  { value: 'sale', label: 'Venda' },
  { value: 'rent', label: 'Aluguel' },
  { value: 'seasonal_rent', label: 'Temporada' },
  { value: 'daily_rent', label: 'Diária' },
  { value: 'lease', label: 'Arrendamento' },
  { value: 'exchange', label: 'Permuta' },
];

const PROPERTY_TYPE_OPTIONS: FilterOption[] = [
  { value: 'house', label: 'Casa' },
  { value: 'apartment', label: 'Apartamento' },
  { value: 'commercial', label: 'Comercial' },
  { value: 'land', label: 'Terreno' },
  { value: 'farm', label: 'Fazenda/Sítio' },
  { value: 'warehouse', label: 'Galpão' },
  { value: 'office', label: 'Sala Comercial' },
  { value: 'studio', label: 'Studio' },
  { value: 'penthouse', label: 'Cobertura' },
  { value: 'townhouse', label: 'Sobrado' },
];

const ITEM_STATUS_OPTIONS: FilterOption[] = [
  { value: 'available', label: 'Disponível' },
  { value: 'unavailable', label: 'Indisponível' },
  { value: 'under_construction', label: 'Em Construção' },
  { value: 'launch', label: 'Lançamento' },
  { value: 'reserved', label: 'Reservado' },
];

const BEDROOMS_OPTIONS: FilterOption[] = [
  { value: '1', label: '1 quarto' },
  { value: '2', label: '2 quartos' },
  { value: '3', label: '3 quartos' },
  { value: '4', label: '4 quartos' },
  { value: '5', label: '5+ quartos' },
];

const BATHROOMS_OPTIONS: FilterOption[] = [
  { value: '1', label: '1 banheiro' },
  { value: '2', label: '2 banheiros' },
  { value: '3', label: '3 banheiros' },
  { value: '4', label: '4+ banheiros' },
];

const PARKING_OPTIONS: FilterOption[] = [
  { value: '1', label: '1 vaga' },
  { value: '2', label: '2 vagas' },
  { value: '3', label: '3 vagas' },
  { value: '4', label: '4+ vagas' },
];

// ═══════════════════════════════════════════════════════
// FILTER DEFINITIONS & CATEGORIES
// ═══════════════════════════════════════════════════════

type FilterType = 'multiselect' | 'singleselect' | 'text' | 'range';

interface FilterDefinition {
  key: string;
  label: string;
  type: FilterType;
  options?: FilterOption[];
  placeholder?: string;
  /** For range filters: label for min input */
  minLabel?: string;
  /** For range filters: label for max input */
  maxLabel?: string;
  /** For range filters: placeholder for min */
  minPlaceholder?: string;
  /** For range filters: placeholder for max */
  maxPlaceholder?: string;
  /** For range filters: unit suffix */
  unit?: string;
}

const FILTER_DEFINITIONS: Record<string, FilterDefinition> = {
  opportunityType: {
    key: 'opportunityType',
    label: 'Operação',
    type: 'multiselect',
    options: OPPORTUNITY_TYPE_OPTIONS,
    placeholder: 'Buscar operação...',
  },
  propertyType: {
    key: 'propertyType',
    label: 'Tipo de Imóvel',
    type: 'multiselect',
    options: PROPERTY_TYPE_OPTIONS,
    placeholder: 'Buscar tipo...',
  },
  itemStatus: {
    key: 'itemStatus',
    label: 'Status',
    type: 'multiselect',
    options: ITEM_STATUS_OPTIONS,
    placeholder: 'Buscar status...',
  },
  bedrooms: {
    key: 'bedrooms',
    label: 'Quartos',
    type: 'singleselect',
    options: BEDROOMS_OPTIONS,
  },
  bathrooms: {
    key: 'bathrooms',
    label: 'Banheiros',
    type: 'singleselect',
    options: BATHROOMS_OPTIONS,
  },
  parkingSpaces: {
    key: 'parkingSpaces',
    label: 'Vagas',
    type: 'singleselect',
    options: PARKING_OPTIONS,
  },
  priceRange: {
    key: 'priceRange',
    label: 'Preço',
    type: 'range',
    minLabel: 'Mínimo',
    maxLabel: 'Máximo',
    minPlaceholder: 'R$ Mín',
    maxPlaceholder: 'R$ Máx',
    unit: 'R$',
  },
  areaRange: {
    key: 'areaRange',
    label: 'Área',
    type: 'range',
    minLabel: 'Mínimo',
    maxLabel: 'Máximo',
    minPlaceholder: 'Mín m²',
    maxPlaceholder: 'Máx m²',
    unit: 'm²',
  },
  city: {
    key: 'city',
    label: 'Cidade',
    type: 'text',
    placeholder: 'Ex: São Paulo, Curitiba...',
  },
  neighborhood: {
    key: 'neighborhood',
    label: 'Bairro',
    type: 'text',
    placeholder: 'Ex: Centro, Batel...',
  },
};

interface FilterCategory {
  id: string;
  label: string;
  icon: IconName;
  filters: string[];
}

const FILTER_CATEGORIES: FilterCategory[] = [
  {
    id: 'type',
    label: 'Tipo',
    icon: 'tag',
    filters: ['opportunityType', 'propertyType', 'itemStatus'],
  },
  {
    id: 'features',
    label: 'Características',
    icon: 'sliders',
    filters: ['bedrooms', 'bathrooms', 'parkingSpaces'],
  },
  {
    id: 'value',
    label: 'Valor',
    icon: 'dollar-sign',
    filters: ['priceRange', 'areaRange'],
  },
  {
    id: 'location',
    label: 'Localização',
    icon: 'map-pin',
    filters: ['city', 'neighborhood'],
  },
];

// ═══════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════

interface ActiveFilter {
  key: string;
  label: string;
  displayValue: string;
}

function formatPriceCompact(value: number): string {
  if (value >= 1000000) return `R$${(value / 1000000).toFixed(1).replace('.', ',')}M`;
  if (value >= 1000) return `R$${(value / 1000).toFixed(0)}mil`;
  return `R$${value}`;
}

function getActiveFilters(filters: PropertiesFilters): ActiveFilter[] {
  const entries: ActiveFilter[] = [];

  // Multi-select filters
  const multiSelectKeys = ['opportunityType', 'propertyType', 'itemStatus'] as const;
  for (const key of multiSelectKeys) {
    const value = filters[key];
    if (!value || !Array.isArray(value) || value.length === 0) continue;
    const def = FILTER_DEFINITIONS[key];
    if (!def?.options) continue;

    const labels = value
      .map(v => def.options!.find(o => o.value === v)?.label)
      .filter(Boolean) as string[];
    if (labels.length === 0) continue;
    const displayValue = labels.length <= 2
      ? labels.join(', ')
      : `${labels[0]}, +${labels.length - 1}`;
    entries.push({ key, label: def.label, displayValue });
  }

  // Single-select number filters (bedrooms, bathrooms, parking)
  const singleSelectMap: Record<string, { filterKey: keyof PropertiesFilters; defKey: string }> = {
    bedrooms: { filterKey: 'minBedrooms', defKey: 'bedrooms' },
    bathrooms: { filterKey: 'minBathrooms', defKey: 'bathrooms' },
    parkingSpaces: { filterKey: 'minParkingSpaces', defKey: 'parkingSpaces' },
  };
  for (const [chipKey, mapping] of Object.entries(singleSelectMap)) {
    const value = filters[mapping.filterKey];
    if (value === undefined || value === null) continue;
    const def = FILTER_DEFINITIONS[mapping.defKey];
    if (!def) continue;
    const option = def.options?.find(o => o.value === String(value));
    entries.push({
      key: chipKey,
      label: def.label,
      displayValue: option?.label || `${value}+`,
    });
  }

  // Price range (composite)
  if (filters.minPrice || filters.maxPrice) {
    let displayValue = '';
    if (filters.minPrice && filters.maxPrice) {
      displayValue = `${formatPriceCompact(filters.minPrice)} - ${formatPriceCompact(filters.maxPrice)}`;
    } else if (filters.minPrice) {
      displayValue = `a partir de ${formatPriceCompact(filters.minPrice)}`;
    } else if (filters.maxPrice) {
      displayValue = `até ${formatPriceCompact(filters.maxPrice)}`;
    }
    entries.push({ key: 'priceRange', label: 'Preço', displayValue });
  }

  // Area range (composite)
  if (filters.minArea || filters.maxArea) {
    let displayValue = '';
    if (filters.minArea && filters.maxArea) {
      displayValue = `${filters.minArea}m² - ${filters.maxArea}m²`;
    } else if (filters.minArea) {
      displayValue = `a partir de ${filters.minArea}m²`;
    } else if (filters.maxArea) {
      displayValue = `até ${filters.maxArea}m²`;
    }
    entries.push({ key: 'areaRange', label: 'Área', displayValue });
  }

  // Text filters
  if (filters.city) {
    entries.push({ key: 'city', label: 'Cidade', displayValue: filters.city });
  }
  if (filters.neighborhood) {
    entries.push({ key: 'neighborhood', label: 'Bairro', displayValue: filters.neighborhood });
  }

  return entries;
}

// ═══════════════════════════════════════════════════════
// PROPS
// ═══════════════════════════════════════════════════════

/**
 * Props do PropertiesAdvancedFilters
 */
export interface PropertiesAdvancedFiltersProps {
  /** Filtros atuais */
  filters: PropertiesFilters;
  /** Callback quando filtros mudam */
  onFiltersChange: (filters: PropertiesFilters) => void;
}

// ═══════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════

/**
 * Filtros avançados de imóveis com design chip-based (estilo Linear/Attio).
 *
 * @example
 * ```tsx
 * <PropertiesAdvancedFiltersComponent
 *   filters={filters}
 *   onFiltersChange={setFilters}
 * />
 * ```
 */
export const PropertiesAdvancedFiltersComponent: React.FC<PropertiesAdvancedFiltersProps> = ({
  filters,
  onFiltersChange,
}) => {
  // ── Popover state ──
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState<'categories' | 'values'>('categories');
  const [selectedFilter, setSelectedFilter] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [optionSearchQuery, setOptionSearchQuery] = useState('');

  // ── Local values for value selector ──
  const [localMultiValues, setLocalMultiValues] = useState<string[]>([]);
  const [localTextValue, setLocalTextValue] = useState('');
  const [localRangeMin, setLocalRangeMin] = useState('');
  const [localRangeMax, setLocalRangeMax] = useState('');

  // ── Positioning ──
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });

  // ── Derived state ──
  const activeFilters = useMemo(() => getActiveFilters(filters), [filters]);
  const hasFilters = activeFilters.length > 0;
  const currentFilterDef = selectedFilter ? FILTER_DEFINITIONS[selectedFilter] : null;

  // ── Update popover position ──
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPopoverPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
      });
    }
  }, [isOpen]);

  // ── Filtered categories for search ──
  const filteredCategories = useMemo(() => {
    if (!searchQuery.trim()) return FILTER_CATEGORIES;
    const query = searchQuery.toLowerCase();
    return FILTER_CATEGORIES
      .map(cat => ({
        ...cat,
        filters: cat.filters.filter(fKey => {
          const def = FILTER_DEFINITIONS[fKey];
          return def?.label.toLowerCase().includes(query);
        }),
      }))
      .filter(cat => cat.filters.length > 0);
  }, [searchQuery]);

  // ── Filtered options for multiselect/singleselect ──
  const filteredOptions = useMemo(() => {
    if (!currentFilterDef?.options) return [];
    if (!optionSearchQuery.trim()) return currentFilterDef.options;
    const query = optionSearchQuery.toLowerCase();
    return currentFilterDef.options.filter(o =>
      o.label.toLowerCase().includes(query)
    );
  }, [currentFilterDef, optionSearchQuery]);

  // ── Map internal key to actual filter keys ──
  const getFilterValue = (filterKey: string): unknown => {
    switch (filterKey) {
      case 'bedrooms': return filters.minBedrooms;
      case 'bathrooms': return filters.minBathrooms;
      case 'parkingSpaces': return filters.minParkingSpaces;
      case 'priceRange': return filters.minPrice || filters.maxPrice ? { min: filters.minPrice, max: filters.maxPrice } : undefined;
      case 'areaRange': return filters.minArea || filters.maxArea ? { min: filters.minArea, max: filters.maxArea } : undefined;
      default: return filters[filterKey as keyof PropertiesFilters];
    }
  };

  // ── Load local values when editing a chip ──
  const loadLocalValues = (filterKey: string) => {
    const def = FILTER_DEFINITIONS[filterKey];
    if (!def) return;

    switch (def.type) {
      case 'multiselect': {
        const value = filters[filterKey as keyof PropertiesFilters];
        setLocalMultiValues(
          Array.isArray(value) ? [...value as string[]] : value ? [value as string] : []
        );
        break;
      }
      case 'singleselect': {
        const numValue = getFilterValue(filterKey) as number | undefined;
        setLocalMultiValues(numValue !== undefined ? [String(numValue)] : []);
        break;
      }
      case 'text': {
        const textVal = filters[filterKey as keyof PropertiesFilters] as string | undefined;
        setLocalTextValue(textVal || '');
        break;
      }
      case 'range': {
        if (filterKey === 'priceRange') {
          setLocalRangeMin(filters.minPrice ? String(filters.minPrice) : '');
          setLocalRangeMax(filters.maxPrice ? String(filters.maxPrice) : '');
        } else if (filterKey === 'areaRange') {
          setLocalRangeMin(filters.minArea ? String(filters.minArea) : '');
          setLocalRangeMax(filters.maxArea ? String(filters.maxArea) : '');
        }
        break;
      }
    }
  };

  // ── Open popover: add new filter ──
  const handleOpenAdd = () => {
    setStep('categories');
    setSelectedFilter(null);
    setSearchQuery('');
    setOptionSearchQuery('');
    setLocalMultiValues([]);
    setLocalTextValue('');
    setLocalRangeMin('');
    setLocalRangeMax('');
    setIsOpen(true);
  };

  // ── Open popover: edit existing chip ──
  const handleEditChip = (filterKey: string) => {
    setStep('values');
    setSelectedFilter(filterKey);
    setOptionSearchQuery('');
    loadLocalValues(filterKey);
    setIsOpen(true);
  };

  // ── Select filter from category menu ──
  const handleSelectCategory = (filterKey: string) => {
    setSelectedFilter(filterKey);
    setOptionSearchQuery('');
    loadLocalValues(filterKey);
    setStep('values');
  };

  // ── Apply selected values ──
  const handleApply = () => {
    if (!selectedFilter) return;
    const def = FILTER_DEFINITIONS[selectedFilter];
    if (!def) return;

    const newFilters = { ...filters };

    switch (def.type) {
      case 'multiselect': {
        if (localMultiValues.length === 0) {
          delete newFilters[selectedFilter as keyof PropertiesFilters];
        } else {
          (newFilters as Record<string, unknown>)[selectedFilter] = localMultiValues;
        }
        break;
      }
      case 'singleselect': {
        const numVal = localMultiValues.length > 0
          ? Math.min(...localMultiValues.map(Number))
          : undefined;
        switch (selectedFilter) {
          case 'bedrooms':
            if (numVal !== undefined) newFilters.minBedrooms = numVal;
            else delete newFilters.minBedrooms;
            break;
          case 'bathrooms':
            if (numVal !== undefined) newFilters.minBathrooms = numVal;
            else delete newFilters.minBathrooms;
            break;
          case 'parkingSpaces':
            if (numVal !== undefined) newFilters.minParkingSpaces = numVal;
            else delete newFilters.minParkingSpaces;
            break;
        }
        break;
      }
      case 'text': {
        if (localTextValue.trim()) {
          (newFilters as Record<string, unknown>)[selectedFilter] = localTextValue.trim();
        } else {
          delete newFilters[selectedFilter as keyof PropertiesFilters];
        }
        break;
      }
      case 'range': {
        if (selectedFilter === 'priceRange') {
          if (localRangeMin) newFilters.minPrice = Number(localRangeMin);
          else delete newFilters.minPrice;
          if (localRangeMax) newFilters.maxPrice = Number(localRangeMax);
          else delete newFilters.maxPrice;
        } else if (selectedFilter === 'areaRange') {
          if (localRangeMin) newFilters.minArea = Number(localRangeMin);
          else delete newFilters.minArea;
          if (localRangeMax) newFilters.maxArea = Number(localRangeMax);
          else delete newFilters.maxArea;
        }
        break;
      }
    }

    onFiltersChange(newFilters);
    setIsOpen(false);
  };

  // ── Handle single-select immediate apply ──
  const handleSingleSelectValue = (value: string) => {
    if (!selectedFilter) return;

    const numVal = Number(value);
    const newFilters = { ...filters };

    // Check if already selected (toggle off)
    const currentVal = getFilterValue(selectedFilter) as number | undefined;
    const isDeselect = currentVal === numVal;

    switch (selectedFilter) {
      case 'bedrooms':
        if (isDeselect) delete newFilters.minBedrooms;
        else newFilters.minBedrooms = numVal;
        break;
      case 'bathrooms':
        if (isDeselect) delete newFilters.minBathrooms;
        else newFilters.minBathrooms = numVal;
        break;
      case 'parkingSpaces':
        if (isDeselect) delete newFilters.minParkingSpaces;
        else newFilters.minParkingSpaces = numVal;
        break;
    }

    onFiltersChange(newFilters);
    setIsOpen(false);
  };

  // ── Remove a chip ──
  const handleRemoveChip = (filterKey: string) => {
    const newFilters = { ...filters };

    switch (filterKey) {
      case 'bedrooms':
        delete newFilters.minBedrooms;
        delete newFilters.maxBedrooms;
        break;
      case 'bathrooms':
        delete newFilters.minBathrooms;
        delete newFilters.maxBathrooms;
        break;
      case 'parkingSpaces':
        delete newFilters.minParkingSpaces;
        delete newFilters.maxParkingSpaces;
        break;
      case 'priceRange':
        delete newFilters.minPrice;
        delete newFilters.maxPrice;
        break;
      case 'areaRange':
        delete newFilters.minArea;
        delete newFilters.maxArea;
        break;
      default:
        delete newFilters[filterKey as keyof PropertiesFilters];
    }

    onFiltersChange(newFilters);
  };

  // ── Clear all ──
  const handleClearAll = () => {
    onFiltersChange({});
  };

  // ── Toggle multiselect value ──
  const handleToggleOption = (value: string) => {
    if (localMultiValues.includes(value)) {
      setLocalMultiValues(localMultiValues.filter(v => v !== value));
    } else {
      setLocalMultiValues([...localMultiValues, value]);
    }
  };

  // ── Close popover ──
  const handleClose = () => {
    setIsOpen(false);
  };

  // ── Back to categories ──
  const handleBack = () => {
    setStep('categories');
    setSelectedFilter(null);
    setSearchQuery('');
  };

  // ── Check if a filter is active ──
  const isFilterActive = (filterKey: string): boolean => {
    const value = getFilterValue(filterKey);
    if (value === undefined || value === null || value === '') return false;
    if (Array.isArray(value) && value.length === 0) return false;
    return true;
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {/* ═══ Active Filter Chips ═══ */}
      {activeFilters.map((filter) => (
        <FilterChip
          key={filter.key}
          label={filter.label}
          value={filter.displayValue}
          onClick={() => handleEditChip(filter.key)}
          onRemove={() => handleRemoveChip(filter.key)}
        />
      ))}

      {/* ═══ Add Filter Button ═══ */}
      <button
        ref={triggerRef}
        type="button"
        onClick={handleOpenAdd}
        data-testid="properties-filter-add"
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200"
        style={{
          backgroundColor: 'var(--theme-button-ghost)',
          color: 'var(--theme-header-text)',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = 'var(--theme-header-button-hover)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost)';
        }}
      >
        <Icon name="plus" className="w-3.5 h-3.5" />
        <span>Filtro</span>
      </button>

      {/* ═══ Clear All Button ═══ */}
      {hasFilters && (
        <button
          type="button"
          onClick={handleClearAll}
          data-testid="properties-filter-clear"
          className="text-xs font-medium px-2 py-1.5 rounded transition-colors"
          style={{ color: 'var(--theme-muted-text)' }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = 'var(--theme-header-text)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = 'var(--theme-muted-text)';
          }}
        >
          Limpar
        </button>
      )}

      {/* ═══ Popover ═══ */}
      {isOpen &&
        createPortal(
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 z-[100]"
              onClick={handleClose}
            />

            {/* Panel */}
            <div
              className="fixed z-[101] w-72 rounded-lg shadow-xl flex flex-col"
              data-testid="properties-filter-popover"
              style={{
                top: `${popoverPosition.top}px`,
                left: `${popoverPosition.left}px`,
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb',
                maxHeight: '420px',
              }}
            >
              {step === 'categories' ? (
                /* ═══════════════════════════════════════ */
                /* STEP 1: CATEGORY LIST                  */
                /* ═══════════════════════════════════════ */
                <>
                  {/* Search */}
                  <div
                    className="p-3 flex-shrink-0"
                    style={{ borderBottom: '1px solid #e5e7eb' }}
                  >
                    <div className="relative">
                      <Icon name="search" className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5"
                        style={{ color: '#9ca3af' }} />
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        data-testid="properties-filter-search"
                        placeholder="Buscar filtro..."
                        className="w-full pl-8 pr-3 py-1.5 text-sm rounded-md border h-[38px]"
                        style={{
                          backgroundColor: '#f9fafb',
                          borderColor: '#e5e7eb',
                          color: '#1e293b',
                        }}
                        autoFocus
                      />
                    </div>
                  </div>

                  {/* Category Groups */}
                  <div className="overflow-y-auto flex-1 py-1">
                    {filteredCategories.length === 0 ? (
                      <div
                        className="text-center py-6 text-sm"
                        style={{ color: '#9ca3af' }}
                      >
                        Nenhum filtro encontrado
                      </div>
                    ) : (
                      filteredCategories.map((category, catIndex) => (
                        <div key={category.id}>
                          {/* Category Header */}
                          <div
                            className="flex items-center gap-2 px-3 py-1.5"
                            style={{
                              color: '#9ca3af',
                              ...(catIndex > 0
                                ? {
                                    borderTop: '1px solid #f3f4f6',
                                    marginTop: '4px',
                                    paddingTop: '8px',
                                  }
                                : {}),
                            }}
                          >
                            <Icon name={category.icon} className="w-3.5 h-3.5" style={{ color: '#9ca3af' }} />
                            <span className="text-[11px] font-semibold uppercase tracking-wider">
                              {category.label}
                            </span>
                          </div>

                          {/* Filter Items */}
                          {category.filters.map((filterKey) => {
                            const def = FILTER_DEFINITIONS[filterKey];
                            if (!def) return null;

                            const active = isFilterActive(filterKey);

                            return (
                              <button
                                key={filterKey}
                                type="button"
                                onClick={() => handleSelectCategory(filterKey)}
                                data-testid={`properties-filter-option-${filterKey}`}
                                className="w-full flex items-center justify-between py-2 text-sm transition-colors text-left"
                                style={{
                                  paddingLeft: '2rem',
                                  paddingRight: '0.75rem',
                                  color: '#1e293b',
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#f9fafb';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                }}
                              >
                                <span>{def.label}</span>
                                {active && (
                                  <div
                                    className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: '#8b5cf6' }}
                                  />
                                )}
                              </button>
                            );
                          })}
                        </div>
                      ))
                    )}
                  </div>
                </>
              ) : (
                /* ═══════════════════════════════════════ */
                /* STEP 2: VALUE SELECTOR                 */
                /* ═══════════════════════════════════════ */
                <>
                  {/* Header with back button */}
                  <div
                    className="flex items-center gap-2 px-3 py-2.5 flex-shrink-0"
                    style={{ borderBottom: '1px solid #e5e7eb' }}
                  >
                    <button
                      type="button"
                      onClick={handleBack}
                      data-testid="properties-filter-back"
                      className="p-1 rounded transition-colors"
                      style={{ color: '#64748b' }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f1f5f9';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <Icon name="chevron-left" className="w-4 h-4" />
                    </button>
                    <span
                      className="text-sm font-semibold"
                      style={{ color: '#1e293b' }}
                    >
                      {currentFilterDef?.label}
                    </span>
                  </div>

                  {/* Value Content */}
                  <div className="overflow-y-auto flex-1">
                    {/* ── MultiSelect ── */}
                    {currentFilterDef?.type === 'multiselect' && (
                      <>
                        {(currentFilterDef.options?.length || 0) > 5 && (
                          <div className="px-3 pt-3 pb-1">
                            <div className="relative">
                              <Icon name="search" className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5"
                                style={{ color: '#9ca3af' }} />
                              <input
                                type="text"
                                value={optionSearchQuery}
                                onChange={(e) => setOptionSearchQuery(e.target.value)}
                                data-testid="properties-filter-option-search"
                                placeholder={currentFilterDef.placeholder || 'Buscar...'}
                                className="w-full pl-8 pr-3 py-1.5 text-sm rounded-md border h-[38px]"
                                style={{
                                  backgroundColor: '#f9fafb',
                                  borderColor: '#e5e7eb',
                                  color: '#1e293b',
                                }}
                                autoFocus
                              />
                            </div>
                          </div>
                        )}

                        <div className="p-2 space-y-0.5">
                          {filteredOptions.length === 0 ? (
                            <div
                              className="text-center py-4 text-sm"
                              style={{ color: '#9ca3af' }}
                            >
                              Nenhuma opção encontrada
                            </div>
                          ) : (
                            filteredOptions.map((option) => {
                              const isSelected = localMultiValues.includes(option.value);

                              return (
                                <button
                                  key={option.value}
                                  type="button"
                                  onClick={() => handleToggleOption(option.value)}
                                  data-testid={`properties-filter-value-${option.value}`}
                                  className="w-full flex items-center justify-between px-3 py-2 rounded text-sm transition-colors text-left"
                                  style={{
                                    backgroundColor: isSelected ? '#f5f3ff' : 'transparent',
                                    color: '#1e293b',
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!isSelected) e.currentTarget.style.backgroundColor = '#f9fafb';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = isSelected ? '#f5f3ff' : 'transparent';
                                  }}
                                >
                                  <span>{option.label}</span>
                                  {isSelected && (
                                    <div
                                      className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
                                      style={{ backgroundColor: '#8b5cf6' }}
                                    >
                                      <Icon name="check" className="w-3 h-3 text-white" />
                                    </div>
                                  )}
                                </button>
                              );
                            })
                          )}
                        </div>

                        {localMultiValues.length > 0 && (
                          <div
                            className="px-3 py-1.5 text-[11px]"
                            style={{ color: '#9ca3af' }}
                          >
                            {localMultiValues.length} selecionado{localMultiValues.length !== 1 ? 's' : ''}
                          </div>
                        )}
                      </>
                    )}

                    {/* ── SingleSelect (bedrooms/bathrooms/parking) ── */}
                    {currentFilterDef?.type === 'singleselect' && (
                      <div className="p-2 space-y-0.5">
                        {currentFilterDef.options?.map((option) => {
                          const currentVal = getFilterValue(selectedFilter!) as number | undefined;
                          const isSelected = currentVal === Number(option.value);

                          return (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() => handleSingleSelectValue(option.value)}
                              data-testid={`properties-filter-value-${option.value}`}
                              className="w-full flex items-center justify-between px-3 py-2 rounded text-sm transition-colors text-left"
                              style={{
                                backgroundColor: isSelected ? '#f5f3ff' : 'transparent',
                                color: '#1e293b',
                              }}
                              onMouseEnter={(e) => {
                                if (!isSelected) e.currentTarget.style.backgroundColor = '#f9fafb';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = isSelected ? '#f5f3ff' : 'transparent';
                              }}
                            >
                              <span>{option.label}</span>
                              {isSelected && (
                                <div
                                  className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
                                  style={{ backgroundColor: '#8b5cf6' }}
                                >
                                  <Icon name="check" className="w-3 h-3 text-white" />
                                </div>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {/* ── Text Input ── */}
                    {currentFilterDef?.type === 'text' && (
                      <div className="p-3">
                        <input
                          type="text"
                          value={localTextValue}
                          onChange={(e) => setLocalTextValue(e.target.value)}
                          data-testid="properties-filter-text"
                          placeholder={currentFilterDef.placeholder}
                          className="w-full px-3 py-2 text-sm rounded-md border h-[38px]"
                          style={{
                            backgroundColor: '#ffffff',
                            borderColor: '#d1d5db',
                            color: '#1e293b',
                          }}
                          onFocus={(e) => {
                            e.currentTarget.style.borderColor = '#8b5cf6';
                          }}
                          onBlur={(e) => {
                            e.currentTarget.style.borderColor = '#d1d5db';
                          }}
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleApply();
                          }}
                        />
                      </div>
                    )}

                    {/* ── Range Input ── */}
                    {currentFilterDef?.type === 'range' && (
                      <div className="p-3 space-y-3">
                        <div>
                          <label
                            className="block text-xs font-medium mb-1"
                            style={{ color: '#64748b' }}
                          >
                            {currentFilterDef.minLabel || 'Mínimo'}
                          </label>
                          <input
                            type="number"
                            value={localRangeMin}
                            onChange={(e) => setLocalRangeMin(e.target.value)}
                            data-testid="properties-filter-range-min"
                            placeholder={currentFilterDef.minPlaceholder || 'Mínimo'}
                            className="w-full px-3 py-2 text-sm rounded-md border h-[38px]"
                            style={{
                              backgroundColor: '#ffffff',
                              borderColor: '#d1d5db',
                              color: '#1e293b',
                            }}
                            onFocus={(e) => {
                              e.currentTarget.style.borderColor = '#8b5cf6';
                            }}
                            onBlur={(e) => {
                              e.currentTarget.style.borderColor = '#d1d5db';
                            }}
                            autoFocus
                          />
                        </div>
                        <div>
                          <label
                            className="block text-xs font-medium mb-1"
                            style={{ color: '#64748b' }}
                          >
                            {currentFilterDef.maxLabel || 'Máximo'}
                          </label>
                          <input
                            type="number"
                            value={localRangeMax}
                            onChange={(e) => setLocalRangeMax(e.target.value)}
                            data-testid="properties-filter-range-max"
                            placeholder={currentFilterDef.maxPlaceholder || 'Máximo'}
                            className="w-full px-3 py-2 text-sm rounded-md border h-[38px]"
                            style={{
                              backgroundColor: '#ffffff',
                              borderColor: '#d1d5db',
                              color: '#1e293b',
                            }}
                            onFocus={(e) => {
                              e.currentTarget.style.borderColor = '#8b5cf6';
                            }}
                            onBlur={(e) => {
                              e.currentTarget.style.borderColor = '#d1d5db';
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Apply Footer (not shown for singleselect - applies immediately) */}
                  {currentFilterDef?.type !== 'singleselect' && (
                    <div
                      className="px-3 py-2.5 flex items-center justify-end flex-shrink-0"
                      style={{ borderTop: '1px solid #e5e7eb' }}
                    >
                      <button
                        type="button"
                        onClick={handleApply}
                        data-testid="properties-filter-apply"
                        className="px-4 py-1.5 text-sm font-medium rounded-md transition-colors"
                        style={{
                          backgroundColor: '#8b5cf6',
                          color: '#ffffff',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#7c3aed';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#8b5cf6';
                        }}
                      >
                        Aplicar
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </>,
          document.body
        )}
    </div>
  );
};

// Backward-compatible export
export const PropertiesAdvancedFilters = PropertiesAdvancedFiltersComponent;

PropertiesAdvancedFiltersComponent.displayName = 'PropertiesAdvancedFiltersComponent';
